import {
  findCsvColumnIndex,
  normalizeCsvHeader,
  parseCsvLine,
  stripExcelTextMarker,
} from "@/lib/csv-utils.js";

const IS_MECH_TRUE = new Set(["true", "mech", "mechanical", "1", "yes"]);
const IS_MECH_FALSE = new Set([
  "false",
  "non-mech",
  "nonmech",
  "non-mechanical",
  "0",
  "no",
]);

/**
 * Parses IsMech CSV cell values into a boolean, or null if blank/unrecognized.
 * Accepts true/false, Mech/Non-Mech, 1/0 (case-insensitive).
 * @param {string|null|undefined} value
 * @returns {boolean|null}
 */
export function parseIsMechCsvValue(value) {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .replace(/[\s_]+/g, "-");

  if (!normalized) return null;
  if (IS_MECH_TRUE.has(normalized)) return true;
  if (IS_MECH_FALSE.has(normalized)) return false;
  return null;
}

/**
 * Stable key for a vendor + message classification row.
 * Does not trim: trailing whitespace is significant (and can appear on unmapped rows).
 * @param {{ Vendor?: string, Message?: string }} row
 * @returns {string}
 */
export function getClassificationRowKey(row) {
  return `${String(row?.Vendor ?? "")}::${String(row?.Message ?? "")}`;
}

/**
 * Parses a classification bulk-import CSV into row objects.
 * Requires the same headers as download:
 * Vendor, Message, DowntimeEventCount, DowntimeHoursCount, IsMech.
 * @param {string} text
 * @returns {{ Vendor: string, Message: string, IsMech: boolean|null }[]}
 * @throws {Error} When the CSV is empty or missing required headers
 */
export function parseClassificationCsv(text) {
  const raw = String(text ?? "").replace(/^\uFEFF/, "");
  const lines = raw
    .split(/\r\n|\n|\r/)
    .map((line) => line.trimEnd())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    throw new Error("CSV file is empty.");
  }

  const headers = parseCsvLine(lines[0]).map(normalizeCsvHeader);
  const requiredHeaders = [
    "vendor",
    "message",
    "downtimeeventcount",
    "downtimehourscount",
    "ismech",
  ];
  const missingRequiredHeader = requiredHeaders.some(
    (name) => findCsvColumnIndex(headers, [name]) < 0
  );
  if (missingRequiredHeader) {
    throw new Error(
      "CSV must include Vendor, Message, DowntimeEventCount, DowntimeHoursCount, and IsMech column headers."
    );
  }

  const vendorIdx = findCsvColumnIndex(headers, ["vendor"]);
  const messageIdx = findCsvColumnIndex(headers, ["message"]);
  const isMechIdx = findCsvColumnIndex(headers, ["ismech"]);

  return lines.slice(1).map((line) => {
    const fields = parseCsvLine(line);
    return {
      Vendor: stripExcelTextMarker(fields[vendorIdx] ?? ""),
      Message: stripExcelTextMarker(fields[messageIdx] ?? ""),
      IsMech: parseIsMechCsvValue(fields[isMechIdx]),
    };
  });
}

/**
 * Builds pending classification selections from CSV rows that match unmapped rows
 * and have a resolved IsMech value.
 * @param {{ Vendor?: string, Message?: string }[]} unmappedRows
 * @param {{ Vendor?: string, Message?: string, IsMech?: boolean|null }[]} csvRows
 * @returns {Record<string, boolean>}
 */
export function buildPendingFromCsvRows(unmappedRows, csvRows) {
  if (!Array.isArray(unmappedRows) || !Array.isArray(csvRows)) return {};

  const unmappedKeys = new Set(
    unmappedRows.map((row) => getClassificationRowKey(row))
  );
  /** @type {Record<string, boolean>} */
  const pending = {};

  for (const csvRow of csvRows) {
    if (csvRow.IsMech !== true && csvRow.IsMech !== false) continue;
    const key = getClassificationRowKey(csvRow);
    if (!unmappedKeys.has(key)) continue;
    pending[key] = csvRow.IsMech;
  }

  return pending;
}

/**
 * Finds the unique, sorted vendor names from classification rows. This is used to populate the vendor filter dropdown
 * in the classification grid.
 * @param {{ Vendor?: string }[]} rows
 * @returns {string[]}
 */
export function getUniqueVendors(rows) {
  if (!Array.isArray(rows)) return [];

  const vendors = rows
    .map((row) => String(row.Vendor ?? "").trim())
    .filter(Boolean);

  return [...new Set(vendors)].sort((a, b) => a.localeCompare(b));
}

/**
 * Human-readable detail string for API / thrown errors (for the error banner).
 * Since this is an internal tool, we can be a bit more verbose than a public-facing app might be.
 * @param {unknown} err
 * @returns {string|undefined}
 */
export function classificationErrorDetail(err) {
  if (err == null) return undefined;
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message || String(err);
  if (typeof err === "object" && "message" in err && err.message != null) {
    return String(err.message);
  }
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

/**
 * Merges pending IsMech selections onto classification rows for display/sort.
 * NOTE: this is implemented only for saving new classifications right now. Once edit is implemented,
 * this can no longer just set isMech to null for rows that don't have a pending selection, since the row may already have an assigned classification.
 * @param {{ Vendor?: string, Message?: string }[]} rows
 * @param {Record<string, boolean>} pendingClassifications - row key → IsMech
 * @returns {(object & { IsMech: boolean|null })[]}
 */
export function mergePendingClassifications(rows, pendingClassifications) {
  if (!Array.isArray(rows)) return [];

  return rows.map((row) => {
    const key = getClassificationRowKey(row);
    const hasPending = Object.prototype.hasOwnProperty.call(
      pendingClassifications,
      key
    );
    return {
      ...row,
      IsMech: hasPending ? pendingClassifications[key] : null,
    };
  });
}

/**
 * Builds the payload for saveDowntimeClassification from pending selections.
 * New mappings use AlarmMessageLookupId = 0.
 * @param {{ Vendor?: string, Message?: string }[]} rows
 * @param {Record<string, boolean>} pendingClassifications
 * @returns {import("@/api/downtime-classification-api.js").ClassificationToSave[]}
 */
export function buildClassificationsToSave(rows, pendingClassifications) {
  if (!Array.isArray(rows)) return [];

  const rowsByKey = new Map(
    rows.map((row) => [getClassificationRowKey(row), row])
  );

  return Object.entries(pendingClassifications)
    .map(([key, isMech]) => {
      const row = rowsByKey.get(key);
      if (!row) return null;
      return {
        AlarmMessageLookupId: 0,
        Message: row.Message,
        Vendor: row.Vendor,
        IsMech: isMech,
      };
    })
    .filter(Boolean);
}

/**
 * Filters classification rows by selected vendors and/or search text.
 * Vendor selection semantics:
 * - `null` / `undefined` — no vendor filter (show all)
 * - `[]` — show none
 * - `string[]` — show only those vendors
 * @param {{ Vendor?: string, Message?: string }[]} rows
 * @param {string} searchTerm
 * @param {string[]|null} [selectedVendors=null]
 * @returns {{ Vendor?: string, Message?: string }[]}
 */
export function filterClassificationRows(
  rows,
  searchTerm,
  selectedVendors = null
) {
  if (!Array.isArray(rows)) return [];

  const term = searchTerm.trim().toLowerCase();
  const hasVendorFilter =
    selectedVendors !== null && selectedVendors !== undefined;
  const vendorSet = hasVendorFilter
    ? new Set(selectedVendors.map((v) => String(v).trim()))
    : null;

  return rows.filter((row) => {
    const vendor = String(row.Vendor ?? "").trim();

    if (vendorSet && !vendorSet.has(vendor)) {
      return false;
    }

    if (!term) return true;

    const message = String(row.Message ?? "").toLowerCase();
    return vendor.toLowerCase().includes(term) || message.includes(term);
  });
}

/**
 * Numeric sort rank for IsMech so assigned rows group together.
 * @param {boolean|null|undefined} isMech
 * @returns {number}
 */
function getIsMechSortValue(isMech) {
  if (isMech === true) return 2;
  if (isMech === false) return 1;
  return 0;
}

/**
 * Coerce a classification count/hours field to a finite number for sorting.
 * @param {unknown} value
 * @returns {number}
 */
function toSortNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value == null || value === "") return 0;
  const parsed = parseFloat(String(value).replace(/[,\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Sorts classification rows by the given column and direction.
 * @param {{ Vendor?: string, Message?: string, DowntimeEventCount?: number, DowntimeHoursCount?: number, IsMech?: boolean|null }[]} rows
 * @param {string|null} column - Column key (Vendor, Message, DowntimeEventCount, DowntimeHoursCount, IsMech)
 * @param {'asc'|'desc'} direction
 * @returns {typeof rows}
 */
export function sortClassificationRows(rows, column, direction) {
  if (!Array.isArray(rows) || !column)
    return Array.isArray(rows) ? [...rows] : [];

  const directionFactor = direction === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    switch (column) {
      case "Vendor": {
        const comparison = String(a.Vendor ?? "").localeCompare(
          String(b.Vendor ?? "")
        );
        return directionFactor * comparison;
      }
      case "Message": {
        const comparison = String(a.Message ?? "").localeCompare(
          String(b.Message ?? "")
        );
        return directionFactor * comparison;
      }
      case "DowntimeEventCount": {
        const aValue = toSortNumber(a.DowntimeEventCount);
        const bValue = toSortNumber(b.DowntimeEventCount);
        return directionFactor * (aValue - bValue);
      }
      case "DowntimeHoursCount": {
        const aValue = toSortNumber(a.DowntimeHoursCount);
        const bValue = toSortNumber(b.DowntimeHoursCount);
        return directionFactor * (aValue - bValue);
      }
      case "IsMech": {
        const aValue = getIsMechSortValue(a.IsMech);
        const bValue = getIsMechSortValue(b.IsMech);
        return directionFactor * (aValue - bValue);
      }
      default:
        return 0;
    }
  });
}
